/**
 * Logs an error message related to the `uiPrefix` function.
 *
 * This function provides detailed error information including the original input,
 * its converted className, and the input type. It's designed to aid in debugging
 * issues with invalid or unexpected inputs to the `uiPrefix` function.
 *
 * @function
 * @param {*} input - The original input that caused the error in the `uiPrefix` function.
 * @param {string} className - The resulting className after processing the input.
 * @returns {void} - This function does not return anything, its primary purpose is to log an error.
 *
 * @example
 * // If the uiPrefix function encounters an invalid object as input:
 * // logs: "Error in uiPrefix: Invalid className provided." with additional error details.
 * throwUiPrefixError({}, "[object Object]");
 */
function throwUiPrefixError(input, className) {
  console.error("Error in uiPrefix: Invalid className provided.", {
    originalInput: input,
    convertedClassName: className,
    inputType: typeof input,
  });
}

/**
 * A utility function that prefixes the given input with 'ui-'.
 *
 * If the input is a string, it's directly used.
 * If the input is an array of strings, they are joined with a space.
 * Other types are attempted to be converted to a string.
 * If an invalid input is provided, an error is logged to the console and an empty string is returned.
 *
 * @function
 * @param {string|string[]|*} input - The input to which 'ui-' should be prefixed.
 * If it's an array of strings, they will be joined with a space.
 * Other types will attempt to be converted to strings.
 * @returns {string} - The modified string with 'ui-' prefixed, or an empty string for invalid inputs.
 *
 * @example
 * // returns 'ui-modal-cancel-btn'
 * uiPrefix('modal-cancel-btn');
 *
 * @example
 * // returns 'ui-button1 button2'
 * uiPrefix(['button1', 'button2']);
 *
 * @example
 * // returns 'ui-123'
 * uiPrefix(123);
 *
 * @example
 * // returns an empty string and logs an error
 * uiPrefix({});
 */
export default function uiPrefix(input) {
  let className;

  // If input is a string and not empty, directly use it.
  if (typeof input === "string" && input.trim() !== "") {
    className = input;
  }
  // If input is an array of strings, join them with a space.
  else if (Array.isArray(input) && input.every((item) => typeof item === "string")) {
    className = input.join(" ");
  }
  // Handle objects as special case and log error
  else if (typeof input === "object" && !Array.isArray(input)) {
    throwUiPrefixError(input, className);
    return "";
  }
  // Try to convert other types to string
  else {
    className = String(input);
  }

  // If after all attempts, className is not a valid string, then log an error.
  if (typeof className !== "string") {
    throwUiPrefixError(input, className);
    return "";
  }

  // Append "ui-" prefix only if className is not an empty string
  return className.trim() !== "" ? `ui-${className}` : "";
}
